// Décisions de fond de cette page : voir PERFORMANCE_MARCHES_GROUND_TRUTH.md (à respecter / challenger avant toute modif).
import React, { useState, useMemo } from 'react';
import {
  XMarkIcon,
  ClipboardDocumentIcon,
  ClockIcon,
  PlusIcon,
  AdjustmentsHorizontalIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  LightBulbIcon,
  BoltIcon,
  EllipsisHorizontalIcon,
  ChartBarIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import type { PriceFormula } from '../8-comparateur/utils/stats';
import {
  MIN_MARCHES_CLOTURES,
  MIN_PERDUS,
  MIN_MARCHES_PERIODE,
  SEUIL_HORS_PORTEE,
  estimerPrixGagnant,
  estPerduHorsPrix,
  pciPerdu,
  labelPciGlobal,
  calculerKpis,
  calculerTendancePci,
  calculerRendementEffort,
  choisirRecommandation,
  simulerPilotage,
  baisseMax,
  repartirBaisse,
  REDUCTION_COUT_MAX,
  margeApresBaisse,
  filtrerParPeriode,
  evaluerRobustesse
} from './performanceMarches.calculs';
import type { Marche, MarcheResultat, ModeFinancement, PeriodeAnalyse } from './performanceMarches.calculs';
import { MARCHES_DEMO } from './performanceMarches.demo';


const MODE_NOTATION_LABELS: Record<PriceFormula, string> = {
  inverse: 'Inverse proportionnelle (moins-disant)',
  linear: 'Linéaire (min-max)',
  subtractive: 'Soustractive'
};

// Couleurs de marque Sillant Design System (cf. tokens.css) — pas dans le thème Tailwind
// partagé de l'appli, donc en valeurs arbitraires pour rester local à cette page.
const BRAND = {
  primary700: '#5B3FB0',
  primary800: '#4A3296',
  primary500: '#7C63D0',
  primary300: '#B8A8EC',
  primary100: '#EEE8FF',
  primary50: '#F6F2FF',
  success700: '#0E7C3A',
  success50: '#E7F5EB',
  ink900: '#2F2F2F',
  ink700: '#4A4A4A',
  ink500: '#6B6B6B',
  ink400: '#9A9A9A',
  ink300: '#C4C4C4',
  ink200: '#ECECEC',
  bgCanvas: '#FAFAF8',
  escGrayBg: '#F1F1EF',
  escMidBd: '#F0D9AE',
  escStrongBd: '#EFC3AA'
};

// Échelle de sévérité à 3 paliers (gris → orange clair → orange foncé, jamais de rouge — cf.
// règle produit sur les indicateurs progressifs). PCI et "Écart requis" sont complémentaires
// (PCI + écart requis = 100 sur un marché perdu), donc la même échelle sert aux deux colonnes.
type Tier = 'gray' | 'mid' | 'strong';
const tierFromPci = (pci: number): Tier => (pci >= 95 ? 'gray' : pci >= 80 ? 'mid' : 'strong');
const tierChipClass = (t: Tier): string =>
  t === 'gray' ? 'bg-[#F1F1EF] text-[#6B6B6B]' : t === 'mid' ? 'bg-[#FDF1DA] text-[#C2760C]' : 'bg-[#FBE3D3] text-[#9A3B0B]';
const tierTextClass = (t: Tier): string =>
  t === 'gray' ? 'text-[#6B6B6B]' : t === 'mid' ? 'text-[#C2760C]' : 'text-[#9A3B0B]';

// PCI global (tous marchés clôturés, cf. ground truth D4) : lecture à 3 paliers.
const textClassPciGlobal = (v: number): string =>
  v >= 95 ? 'text-[#0E7C3A]' : v >= 85 ? 'text-[#C2760C]' : 'text-[#9A3B0B]';

// Période d'analyse du cockpit (ground truth D16).
const LIBELLES_PERIODE: Record<PeriodeAnalyse, { court: string; phrase: string }> = {
  '12m': { court: '12 mois', phrase: '12 derniers mois' },
  '24m': { court: '24 mois', phrase: '24 derniers mois' },
  tous: { court: 'Tout', phrase: 'tout l\'historique' }
};

const formaterDate = (dateRemise: string): string => {
  const [a, m, j] = dateRemise.split('-');
  return `${j}/${m}/${a}`;
};

const PerformanceMarches: React.FC = () => {
  const [marches, setMarches] = useState<Marche[]>(MARCHES_DEMO);

  const [selectedMarche, setSelectedMarche] = useState<Marche | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [raoText, setRaoText] = useState<string>('');
  const [showRaoModal, setShowRaoModal] = useState(false);
  const [selectedMarcheForCourrier, setSelectedMarcheForCourrier] = useState<Marche | null>(null);
  const [showCourrierModal, setShowCourrierModal] = useState(false);
  const [courrierText, setCourrierText] = useState<string>('');
  const [hoveredTooltip, setHoveredTooltip] = useState<string | null>(null);

  // Pilotage de la marge
  const [margeBase, setMargeBase] = useState('15');
  // Simulateur : l'utilisateur choisit une baisse de prix et comment la financer (D8).
  const [baissePrix, setBaissePrix] = useState(0);
  const [modeFinancement, setModeFinancement] = useState<ModeFinancement>('marge');

  // Liste des marchés : filtre, recherche, tri, pagination, sélection
  const [filtreResultat, setFiltreResultat] = useState<'Tous' | MarcheResultat>('Tous');
  const [recherche, setRecherche] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 8;
  const [triColonne, setTriColonne] = useState<'ecart' | 'pci' | null>(null);
  const [triSens, setTriSens] = useState<'asc' | 'desc'>('desc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // États pour le formulaire de complétion
  const [formData, setFormData] = useState({
    resultat: 'Perdu' as MarcheResultat,
    tonPrix: '',
    notePrixObtenue: '',
    notePrixMaximale: '',
    modeNotation: 'inverse' as PriceFormula,
    dateRemise: '',
    prixAttributaire: ''
  });

  // Période d'analyse (ground truth D16) : tout le cockpit est calculé sur ces marchés ; la liste
  // des marchés reste complète.
  const [periode, setPeriode] = useState<PeriodeAnalyse>('24m');
  const marchesPeriode = useMemo(() => filtrerParPeriode(marches, periode), [marches, periode]);

  // Indicateurs du cockpit (ground truth D4, D9)
  const kpis = useMemo(() => calculerKpis(marchesPeriode), [marchesPeriode]);

  // Évolution du PCI global par semestre de remise (ground truth D11)
  const pciTrend = useMemo(() => calculerTendancePci(marchesPeriode), [marchesPeriode]);

  // Pilotage de la marge (ground truth D5, D7, D8, D9) : simulateur à la baisse uniquement,
  // sur les perdus à portée. Toute la logique est dans simulerPilotage (testée).
  const margeBaseNum = parseFloat(margeBase.replace(',', '.')) || 0;
  const baisseMaxMode = baisseMax(margeBaseNum, modeFinancement);
  const reglage = repartirBaisse(margeBaseNum, baissePrix, modeFinancement);
  const pilotageMarge = useMemo(
    () => simulerPilotage(marchesPeriode, margeBaseNum, reglage.margeSimulee, reglage.reductionCout),
    [marchesPeriode, margeBaseNum, reglage.margeSimulee, reglage.reductionCout]
  );
  const LIBELLES_FINANCEMENT: Record<ModeFinancement, { court: string; phrase: string }> = {
    marge: { court: 'Sur ma marge', phrase: 'en rognant votre marge' },
    cout: { court: 'Par des économies', phrase: 'par des économies sur vos coûts' },
    mix: { court: 'Les deux', phrase: `par des économies (jusqu'à ${REDUCTION_COUT_MAX} %) puis votre marge` }
  };
  const choisirMode = (mode: ModeFinancement) => {
    setModeFinancement(mode);
    setBaissePrix(b => Math.min(b, baisseMax(margeBaseNum, mode)));
  };

  // Rendement de l'effort (ground truth D6)
  const rendementEffort = useMemo(() => calculerRendementEffort(marchesPeriode, margeBaseNum), [marchesPeriode, margeBaseNum]);

  // Diagnostic (ground truth D6, D9, D17) : tiré du tableau de rendement, pas d'une moyenne d'écarts.
  // On retient le palier au meilleur résultat net positif, en privilégiant le levier marge
  // (sans effort opérationnel) quand il est rentable, sinon le levier coût. Formulé comme une
  // consigne pour les prochaines offres, avec le chiffre historique en appui.
  const diagnostic = useMemo(() => {
    if (!rendementEffort || kpis.pciGlobal === null || kpis.nombreMarchesPciGlobal < MIN_MARCHES_CLOTURES) return null;
    const k = (v: number) => `${Math.round(v / 1000).toLocaleString('fr-FR')} k€`;
    const pct = (v: number) => `${v.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`;
    const consigne = (levier: 'marge' | 'cout', palier: number) =>
      levier === 'marge'
        ? `baissez vos prix de ${palier} % en prenant sur votre marge (${pct(margeBaseNum)} → ${pct(margeApresBaisse(margeBaseNum, palier))})`
        : `baissez vos prix de ${palier} % en réduisant vos coûts d'autant (marge conservée à ${pct(margeBaseNum)})`;
    const choix = choisirRecommandation(rendementEffort);
    let recommandation: string;
    if (choix.levier) {
      const net = choix.levier === 'marge' ? choix.ligne.netLevierMarge : choix.ligne.netLevierCout;
      recommandation = `Pour vos prochaines offres : ${consigne(choix.levier, choix.ligne.palier)}.`
        + (choix.levier === 'cout' ? ' Rogner la marge ne paie à aucun palier.' : '')
        + ` Sur votre historique : +${choix.ligne.marchesEnPlus} marché(s), +${k(net)} net.`;
    } else {
      recommandation = "Pour vos prochaines offres : gardez votre formule actuelle. Aucune baisse ne paie sur votre historique : les marchés récupérés ne compensent pas la marge perdue sur vos gagnés.";
    }
    let alerte: string | null = null;
    const robustesse = evaluerRobustesse(marchesPeriode, margeBaseNum);
    if (robustesse?.fragile) {
      const { marcheDecisif: m, sansLui } = robustesse;
      const nom = `« ${m.nom} » (${k(m.tonPrix)})`;
      const autre = sansLui ? `${consigne(sansLui.levier, sansLui.palier)}` : 'gardez votre formule actuelle';
      alerte = robustesse.raison === 'un-seul-marche'
        ? `Recommandation fragile : elle ne repose que sur ${nom}. Sans lui : ${autre}.`
        : `Recommandation fragile : elle dépend de ${nom}. Sans lui : ${autre}.`;
    }
    return { titre: labelPciGlobal(kpis.pciGlobal), pciGlobal: kpis.pciGlobal, recommandation, alerte };
  }, [rendementEffort, kpis.pciGlobal, kpis.nombreMarchesPciGlobal, marchesPeriode, margeBaseNum]);


  // Écart requis : de combien il aurait fallu baisser SON offre (pas le % benchmark vs le gagnant)
  const calculerEffortRequis = (marche: Marche): { eur: number; pct: number } | null => {
    if (marche.resultat !== 'Perdu' || !marche.prixGagnant || marche.prixGagnant >= marche.tonPrix) {
      return null;
    }
    const eur = marche.tonPrix - marche.prixGagnant;
    return { eur, pct: (eur / marche.tonPrix) * 100 };
  };

  // Obtenir l'interprétation du PCI (borné à 100 : calculé sur les pertes uniquement,
  // on ne peut jamais être "plus compétitif que le gagnant" sur un marché perdu)
  const getPciInterpretation = (pci: number): string => {
    if (pci >= 95) return 'Perdu de justesse';
    if (pci >= 80) return 'Écart modéré';
    return 'Trop cher';
  };

  // Couleur du delta de marge/PCI simulé : indicateur progressif, pas d'alarme rouge
  const getMargeDeltaColor = (deltaPct: number): string => {
    if (deltaPct >= 0) return 'text-[#0E7C3A]';
    if (deltaPct >= -5) return 'text-[#6B6B6B]';
    if (deltaPct >= -15) return 'text-[#C2760C]';
    return 'text-[#9A3B0B] font-semibold';
  };

  // Formater un nombre pour l'input
  const formaterNombre = (valeur: string): string => {
    let nettoye = valeur.replace(/[^\d,.]/g, '');
    nettoye = nettoye.replace(',', '.');
    const parties = nettoye.split('.');
    if (parties.length > 2) {
      nettoye = parties[0] + '.' + parties.slice(1).join('');
    }
    return nettoye;
  };

  // Ouvrir la modal de complétion
  const handleCompleter = (marche: Marche) => {
    setSelectedMarche(marche);
    setFormData({
      resultat: marche.resultat === 'En attente' ? 'Perdu' : marche.resultat,
      tonPrix: marche.tonPrix.toString(),
      notePrixObtenue: marche.notePrixObtenue?.toString() || '',
      notePrixMaximale: marche.notePrixMaximale?.toString() || '',
      modeNotation: marche.modeNotation || 'inverse',
      dateRemise: marche.dateRemise || '',
      prixAttributaire: marche.prixAttributaire?.toString() || ''
    });
    setIsModalOpen(true);
  };

  // Fermer la modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMarche(null);
    setFormData({
      resultat: 'Perdu',
      tonPrix: '',
      notePrixObtenue: '',
      notePrixMaximale: '',
      modeNotation: 'inverse',
      dateRemise: '',
      prixAttributaire: ''
    });
  };

  // Sauvegarder les données du marché
  const handleSave = () => {
    if (!selectedMarche) return;

    const tonPrix = parseFloat(formData.tonPrix.replace(/\s/g, '').replace(',', '.'));
    const notePrixObtenue = parseFloat(formData.notePrixObtenue.replace(/\s/g, '').replace(',', '.'));
    const notePrixMaximale = parseFloat(formData.notePrixMaximale.replace(/\s/g, '').replace(',', '.'));

    if (isNaN(tonPrix) || tonPrix <= 0) {
      alert('Veuillez saisir un prix valide');
      return;
    }
    if (!formData.dateRemise) {
      alert('Veuillez saisir la date de remise de l\'offre');
      return;
    }

    let prixGagnant: number | undefined;
    let ecart: number | undefined;
    let sourcePrixReference: Marche['sourcePrixReference'];
    const prixAttributaireSaisi = parseFloat(formData.prixAttributaire.replace(/\s/g, '').replace(',', '.'));
    const prixAttributaire = formData.resultat === 'Perdu' && prixAttributaireSaisi > 0 ? prixAttributaireSaisi : undefined;

    if (formData.resultat === 'Perdu') {
      if (prixAttributaire !== undefined) {
        // Ground truth D2 : le prix de l'attributaire, quand il est connu, prime sur l'estimation
        // via la note prix (qui ne donne que le prix le plus bas de la consultation).
        prixGagnant = prixAttributaire;
        sourcePrixReference = 'communique';
        ecart = ((tonPrix - prixGagnant) / prixGagnant) * 100;
      } else if (notePrixObtenue > 0 && notePrixMaximale > 0) {
        sourcePrixReference = 'estime';
        // Calcul du prix gagnant estimé selon le mode de notation du marché
        prixGagnant = estimerPrixGagnant(tonPrix, notePrixObtenue, notePrixMaximale, formData.modeNotation);

        // Calcul de l'écart
        if (prixGagnant && prixGagnant > 0) {
          ecart = ((tonPrix - prixGagnant) / prixGagnant) * 100;
        }
      }
    } else if (formData.resultat === 'Gagné') {
      prixGagnant = tonPrix;
      ecart = 0;

    }

    // Mettre à jour le marché
    setMarches(prevMarches =>
      prevMarches.map(m =>
        m.id === selectedMarche.id
          ? {
              ...m,
              resultat: formData.resultat,
              tonPrix,
              prixGagnant,
              ecart,
              notePrixObtenue: formData.resultat === 'Perdu' && !isNaN(notePrixObtenue) ? notePrixObtenue : undefined,
              notePrixMaximale: formData.resultat === 'Perdu' && !isNaN(notePrixMaximale) ? notePrixMaximale : undefined,
              prixAttributaire,
              sourcePrixReference,
              modeNotation: formData.resultat === 'Perdu' ? formData.modeNotation : undefined,
              dateRemise: formData.dateRemise
            }
          : m
      )
    );

    handleCloseModal();
  };

  // Copier le texte RAO
  const handleCopierRao = () => {
    navigator.clipboard.writeText(raoText);
    alert('Texte copié dans le presse-papiers !');
  };

  // Ouvrir la modal de courrier d'attribution
  const handleOpenCourrierModal = (marche: Marche, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation(); // Empêcher la propagation si on clique sur le bouton
    }
    // Ne pas ouvrir si le marché a déjà un prix de référence (communiqué ou estimé)
    if (marche.prixGagnant !== undefined) {
      return;
    }
    setSelectedMarcheForCourrier(marche);

    // Générer le texte du courrier
    const texte = `Madame, Monsieur,

Nous accusons réception de la notification relative à l'attribution du marché ${marche.nom}, pour lequel notre société a remis une offre.

Conformément aux dispositions de l'article R2181-1 du Code de la commande publique, nous vous remercions par avance de bien vouloir nous communiquer les éléments suivants concernant l'analyse des offres :

- le classement de notre offre,
- les notes détaillées obtenues par notre offre pour chacun des critères de jugement,
- les notes de l'offre retenue,
- ainsi que les caractéristiques et avantages de l'offre attributaire.

Ces éléments nous permettront d'améliorer la qualité de nos réponses lors de prochaines consultations.

Nous vous remercions par avance de l'attention portée à notre demande et restons à votre disposition pour toute précision complémentaire.

Veuillez agréer, Madame, Monsieur, l'expression de nos salutations distinguées.`;

    setCourrierText(texte);
    setShowCourrierModal(true);
  };

  // Copier le texte du courrier
  const handleCopierCourrier = () => {
    navigator.clipboard.writeText(courrierText);
    alert('Texte copié dans le presse-papiers !');
  };

  const toggleTri = (col: 'ecart' | 'pci') => {
    if (triColonne === col) {
      setTriSens(s => (s === 'asc' ? 'desc' : 'asc'));
    } else {
      setTriColonne(col);
      setTriSens('desc');
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Liste filtrée / recherchée / triée pour le tableau
  const marchesAffiches = useMemo(() => {
    let liste = marches;
    if (filtreResultat !== 'Tous') {
      liste = liste.filter(m => m.resultat === filtreResultat);
    }
    const q = recherche.trim().toLowerCase();
    if (q) {
      liste = liste.filter(m => m.nom.toLowerCase().includes(q) || m.acheteur.toLowerCase().includes(q));
    }
    if (triColonne) {
      liste = [...liste].sort((a, b) => {
        const va = triColonne === 'pci' ? (pciPerdu(a) ?? -1) : (calculerEffortRequis(a)?.pct ?? -1);
        const vb = triColonne === 'pci' ? (pciPerdu(b) ?? -1) : (calculerEffortRequis(b)?.pct ?? -1);
        return triSens === 'asc' ? va - vb : vb - va;
      });
    }
    return liste;
  }, [marches, filtreResultat, recherche, triColonne, triSens]);

  const totalPages = Math.max(1, Math.ceil(marchesAffiches.length / PAGE_SIZE));
  const pageActuelle = Math.min(page, totalPages);
  const marchesPage = marchesAffiches.slice((pageActuelle - 1) * PAGE_SIZE, pageActuelle * PAGE_SIZE);

  return (
    <div className="min-h-screen" style={{ background: BRAND.bgCanvas }}>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 flex-wrap py-2 mb-4">
          <h1 className="text-[22px] font-semibold tracking-tight m-0 flex items-center gap-2 flex-wrap" style={{ color: BRAND.ink900 }}>
            Performance marchés
            <span className="text-[13px] font-normal" style={{ color: BRAND.ink500 }}>
              · où vous perdez sur le prix, et de combien baisser pour regagner
            </span>
          </h1>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="inline-flex items-center gap-2 h-[34px] pl-3 pr-[3px] border rounded-[9px] bg-white text-[12.5px]" style={{ borderColor: BRAND.ink200, color: BRAND.ink500 }} title="Période d'analyse du cockpit, par date de remise. La liste des marchés reste complète.">
              <CalendarIcon className="w-3.5 h-3.5" style={{ color: BRAND.ink400 }} />
              <span className="tabular-nums">
                <b style={{ color: BRAND.ink900 }} className="font-semibold">{marchesPeriode.length}</b>
                {periode !== 'tous' && <> / {marches.length}</>} marchés
              </span>
              <span className="inline-flex gap-[2px] p-[2px] rounded-[7px]" style={{ background: '#F7F7F7' }}>
                {(['12m', '24m', 'tous'] as const).map(p => (
                  <button
                    key={p}
                    onClick={() => setPeriode(p)}
                    className="h-[24px] px-2 rounded-[5px] text-[12px] font-medium transition-colors"
                    style={periode === p ? { background: 'white', color: BRAND.primary700, fontWeight: 600, boxShadow: '0 1px 2px rgba(47,47,47,0.05)' } : { color: BRAND.ink500 }}
                  >
                    {LIBELLES_PERIODE[p].court}
                  </button>
                ))}
              </span>
            </span>
            <button
              className="inline-flex items-center gap-2 h-[34px] px-3 border rounded-[9px] bg-white text-[12.5px] hover:bg-gray-50 transition-colors"
              style={{ borderColor: BRAND.ink300, color: BRAND.ink900 }}
              title="Export non câblé — design uniquement"
            >
              <ArrowDownTrayIcon className="w-3.5 h-3.5" style={{ color: BRAND.ink500 }} />
              Exporter
            </button>
            <div className="relative flex-shrink-0">
              <button
                onMouseEnter={() => setHoveredTooltip('page-help')}
                onMouseLeave={() => setHoveredTooltip(null)}
                className="w-7 h-7 rounded-full bg-white border border-gray-300 hover:bg-gray-50 flex items-center justify-center text-sm font-medium cursor-help transition-colors shadow-sm"
                style={{ color: BRAND.ink500 }}
              >
                ?
              </button>
              {hoveredTooltip === 'page-help' && (
                <div className="absolute z-50 w-[420px] max-h-[75vh] overflow-y-auto px-4 py-4 text-xs text-white bg-gray-900 rounded-lg shadow-lg top-full right-0 mt-2 whitespace-normal space-y-4">
                  <div>
                    <div className="font-semibold text-sm mb-1">Période</div>
                    <ul className="space-y-1 text-gray-300">
                      <li><span className="text-white font-medium">12 mois / 24 mois / Tout</span> : tout le cockpit (indicateurs, simulateur, recommandation) est calculé sur les marchés remis dans la période. La liste des marchés reste complète.</li>
                    </ul>
                  </div>
                  <div>
                    <div className="font-semibold text-sm mb-1">État concurrentiel</div>
                    <ul className="space-y-1 text-gray-300">
                      <li><span className="text-white font-medium">Taux de succès</span> : marchés gagnés / marchés clôturés (les "En attente" ne comptent pas).</li>
                      <li><span className="text-white font-medium">Marge de progression</span> : effort médian (en % de votre prix) qu'il aurait fallu consentir sur vos marchés perdus à portée (PCI ≥ {SEUIL_HORS_PORTEE}). Les perdus plus loin sont comptés « hors de portée », ceux où vous étiez déjà le moins cher « hors prix ».</li>
                      <li><span className="text-white font-medium">PCI (note globale)</span> : moyenne sur vos marchés clôturés — 100 par marché gagné, prix de référence ÷ votre prix × 100 par marché perdu. ≥ 95 : compétitif. 85-95 : dans le marché. {'<'} 85 : hors marché. En dessous, le PCI des seuls perdus indique de combien vous perdez. Le petit graphique suit le PCI global par semestre de remise (nombre de marchés entre parenthèses ; tendance calculée si chaque semestre compte au moins {MIN_MARCHES_PERIODE} marchés).</li>
                    </ul>
                  </div>
                  <div>
                    <div className="font-semibold text-sm mb-1">Pilotage de la marge</div>
                    <ul className="space-y-1 text-gray-300">
                      <li>1. Renseignez votre <span className="text-white font-medium">marge habituelle</span> (% sur coût de revient : prix = coût × (1 + marge)).</li>
                      <li>2. Choisissez comment financer la baisse : <span className="text-white font-medium">sur votre marge</span>, <span className="text-white font-medium">par des économies de coût</span> (jusqu'à {REDUCTION_COUT_MAX} %), ou les deux.</li>
                      <li>3. Déplacez le curseur de baisse de prix : chaque point est un marché perdu à portée, il devient vert quand vous le dépassez.</li>
                      <li><span className="text-white font-medium">Recommandation</span> : la baisse à appliquer à vos prochaines offres, tirée du rendement de l'effort. Signalée « fragile » si elle ne repose que sur un marché, ou si elle change quand on retire le plus gros marché récupéré.</li>
                      <li><span className="text-white font-medium">Rendement de l'effort</span> : pour chaque palier de baisse appliqué à tous vos marchés, le taux de succès atteignable et le résultat net en marge (marchés récupérés − marge perdue sur les marchés déjà gagnés), par levier.</li>
                      <li>La baisse s'applique à tous vos prix : les marchés déjà gagnés restent gagnés mais rapportent moins. Les perdus hors de portée (PCI {'<'} {SEUIL_HORS_PORTEE}) ne sont pas simulés.</li>
                    </ul>
                  </div>
                  <div>
                    <div className="font-semibold text-sm mb-1">Liste des marchés</div>
                    <ul className="space-y-1 text-gray-300">
                      <li><span className="text-white font-medium">Écart requis</span> : de combien il fallait baisser VOTRE prix (% et €) pour gagner — l'indicateur le plus actionnable pour calibrer votre prochaine offre.</li>
                      <li>Le bouton <span className="text-white font-medium">Challenger</span> génère une demande de communication des notes auprès de l'acheteur.</li>
                    </ul>
                  </div>
                  <div className="text-gray-400 text-[11px] pt-1 border-t border-gray-700">
                    "Exporter" et "Filtrer" sont pour l'instant des maquettes visuelles, non câblées à une action réelle.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ══ COCKPIT ══ */}
        {/* Une ligne : État concurrentiel (1/3) + Pilotage de la marge (2/3, en deux colonnes de 1/3) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3.5 mb-3.5 items-stretch">
          {/* État concurrentiel */}
          {/* Pas d'overflow-hidden ici : il coupait l'infobulle du PCI. Les coins arrondis sont portés par l'encart du diagnostic. */}
          <div className="bg-white border rounded-[14px] flex flex-col" style={{ borderColor: BRAND.ink200 }}>
            <div className="flex items-center gap-2 px-4 py-[11px] border-b text-[11px] font-bold uppercase tracking-wide" style={{ borderColor: BRAND.ink200, color: BRAND.ink400 }}>
              <span className="w-[22px] h-[22px] rounded-md grid place-content-center" style={{ background: '#FDF1DA', color: '#C2760C' }}>
                <ChartBarIcon className="w-3.5 h-3.5" />
              </span>
              État concurrentiel
              <span className="ml-auto font-medium normal-case tracking-normal" style={{ color: BRAND.ink400 }}>sur marchés clôturés · {LIBELLES_PERIODE[periode].phrase}</span>
            </div>

            <div className="flex flex-col flex-1">
              {/* PCI + graphique */}
              <div className="border-b px-4 pt-3 pb-[14px] flex flex-col" style={{ borderColor: BRAND.ink200 }}>
                <div className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wide" style={{ color: BRAND.ink400 }}>
                  Indice de compétitivité (PCI)
                  <span className="relative">
                    <button
                      onMouseEnter={() => setHoveredTooltip('pci-tooltip')}
                      onMouseLeave={() => setHoveredTooltip(null)}
                      className="w-[15px] h-[15px] rounded-full border flex items-center justify-center text-[8px] cursor-help"
                      style={{ borderColor: BRAND.ink200, color: BRAND.ink400 }}
                    >
                      ?
                    </button>
                    {hoveredTooltip === 'pci-tooltip' && (
                      <div className="absolute z-50 w-64 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg top-full left-1/2 -translate-x-1/2 mt-2 whitespace-normal normal-case tracking-normal font-normal">
                        <div className="space-y-1">
                          <div>Note globale sur vos marchés clôturés : 100 par marché gagné (ou perdu hors prix), prix de référence ÷ votre prix × 100 par marché perdu.</div>
                          <div>≥ 95 : Compétitif · 85-95 : Dans le marché · {'<'} 85 : Hors marché</div>
                          <div className="text-gray-400">Plafonne à 100 : l'avance réelle sur un marché gagné est inconnue.</div>
                        </div>
                      </div>
                    )}
                  </span>
                </div>

                {kpis.pciGlobal !== null && kpis.nombreMarchesPciGlobal >= MIN_MARCHES_CLOTURES ? (
                  <>
                    <div className="flex items-baseline gap-[13px] mt-[9px]">
                      <span className={`text-[38px] font-semibold tracking-tight leading-none tabular-nums ${textClassPciGlobal(kpis.pciGlobal)}`}>
                        {kpis.pciGlobal.toFixed(0)}
                      </span>
                      <span className="flex flex-col gap-[3px]">
                        <span className={`inline-flex items-center gap-1.5 text-[12px] font-semibold ${textClassPciGlobal(kpis.pciGlobal)}`}>
                          <span className="w-[7px] h-[7px] rounded-full" style={{ background: 'currentColor' }} />
                          {labelPciGlobal(kpis.pciGlobal)}
                        </span>
                        <span className="text-[11px] tabular-nums" style={{ color: BRAND.ink500 }}>
                          sur {kpis.nombreMarchesPciGlobal} marchés clôturés
                        </span>
                      </span>
                    </div>
                    <div className="text-[11px] mt-2 tabular-nums" style={{ color: BRAND.ink500 }}>
                      {kpis.nombrePerdusAvecReference >= MIN_PERDUS ? (
                        <>
                          Sur les {kpis.nombrePerdusAvecReference} perdus : PCI <span className={`font-semibold ${tierTextClass(tierFromPci(kpis.pciMoyen))}`}>{kpis.pciMoyen.toFixed(0)}</span>
                          {' · '}{getPciInterpretation(kpis.pciMoyen).toLowerCase()}
                        </>
                      ) : (
                        <>Sur les perdus : pas assez de données ({kpis.nombrePerdusAvecReference}/{MIN_PERDUS})</>
                      )}
                    </div>

                    {pciTrend && (
                      <div className="mt-auto pt-3">
                        <div className="flex items-center justify-between text-[10.5px] mb-1.5" style={{ color: BRAND.ink400 }}>
                          <span>
                            Évolution par semestre de remise
                            {pciTrend.derniere && pciTrend.precedente && <> · {pciTrend.derniere.label} vs {pciTrend.precedente.label}</>}
                          </span>
                          {pciTrend.derniere && pciTrend.precedente ? (
                            <span className={`inline-flex items-center gap-1 font-semibold ${getMargeDeltaColor(pciTrend.derniere.pci - pciTrend.precedente.pci)}`}>
                              {pciTrend.derniere.pci >= pciTrend.precedente.pci ? '▲' : '▼'} {Math.abs(pciTrend.derniere.pci - pciTrend.precedente.pci).toFixed(0)} pt
                            </span>
                          ) : (
                            <span>tendance : pas assez de données</span>
                          )}
                        </div>
                        <ResponsiveContainer width="100%" height={90}>
                          <LineChart data={pciTrend.historique} margin={{ top: 4, right: 2, left: 2, bottom: 0 }}>
                            <XAxis dataKey="label" hide />
                            <YAxis domain={[0, 100]} hide />
                            <RechartsTooltip
                              formatter={(value: number) => [value.toFixed(0), 'PCI global']}
                              labelFormatter={(_, payload) => {
                                const point = payload?.[0]?.payload;
                                return point ? `${point.label} · ${point.n} marché${point.n > 1 ? 's' : ''}` : '';
                              }}
                              contentStyle={{ borderRadius: '8px', border: `1px solid ${BRAND.ink200}`, fontSize: '12px' }}
                            />
                            {pciTrend.precedente && (
                              <ReferenceLine y={pciTrend.precedente.pci} stroke={BRAND.escMidBd} strokeDasharray="3 4" />
                            )}
                            <Line type="monotone" dataKey="pci" stroke={BRAND.primary700} strokeWidth={2.5} dot={{ r: 2.5, fill: BRAND.primary700 }} activeDot={{ r: 4, fill: BRAND.primary700 }} />
                          </LineChart>
                        </ResponsiveContainer>
                        <div className="flex justify-between text-[9px] font-mono mt-[5px]" style={{ color: BRAND.ink400 }}>
                          {pciTrend.historique.map(p => (
                            <span key={p.cle} style={p.n < MIN_MARCHES_PERIODE ? { opacity: 0.5 } : undefined}>
                              {p.label} ({p.n})
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-sm mt-3" style={{ color: BRAND.ink400 }}>
                    Pas assez de données ({kpis.nombreMarchesPciGlobal}/{MIN_MARCHES_CLOTURES} marchés clôturés)
                  </div>
                )}
              </div>
              {/* KPI + alerte */}
              <div className="flex flex-col flex-1">
                <div className="grid grid-cols-2">
                  <div className="px-4 py-3 flex flex-col justify-center">
                    <div className="text-[10.5px] font-semibold uppercase tracking-wide" style={{ color: BRAND.ink400 }}>Taux de succès</div>
                    <div className="text-[28px] font-semibold tracking-tight leading-none mt-[7px] tabular-nums" style={{ color: BRAND.ink900 }}>
                      {kpis.nombreMarchesFermes >= MIN_MARCHES_CLOTURES ? kpis.tauxSucces.toFixed(0) : '—'}
                      <span className="text-[15px] font-medium" style={{ color: BRAND.ink500 }}>&nbsp;%</span>
                    </div>
                    <div className="h-[5px] rounded-full overflow-hidden mt-[9px]" style={{ background: BRAND.ink200 }}>
                      <span className="block h-full rounded-full" style={{ width: `${kpis.nombreMarchesFermes >= MIN_MARCHES_CLOTURES ? Math.min(100, kpis.tauxSucces) : 0}%`, background: BRAND.primary500 }} />
                    </div>
                    <div className="text-[11px] mt-[6px] tabular-nums" style={{ color: BRAND.ink500 }}>
                      {kpis.nombreMarchesFermes >= MIN_MARCHES_CLOTURES
                        ? `${kpis.nombreMarchesGagnes} gagnés / ${kpis.nombreMarchesFermes} clôturés`
                        : `Pas assez de données (${kpis.nombreMarchesFermes}/${MIN_MARCHES_CLOTURES} clôturés)`}
                    </div>
                  </div>
                  <div className="px-4 py-3 border-l flex flex-col justify-center" style={{ borderColor: BRAND.ink200 }}>
                    <div className="text-[10.5px] font-semibold uppercase tracking-wide" style={{ color: BRAND.ink400 }}>Marge de progression</div>
                    <div className="text-[28px] font-semibold tracking-tight leading-none mt-[7px] tabular-nums" style={{ color: BRAND.ink900 }}>
                      {kpis.effortMedianAPortee !== null && kpis.nombrePerdusAPortee >= MIN_PERDUS ? `−${kpis.effortMedianAPortee.toFixed(1)}` : '—'}
                      <span className="text-[15px] font-medium" style={{ color: BRAND.ink500 }}>&nbsp;%</span>
                    </div>
                    <div className="text-[11px] mt-[6px]" style={{ color: BRAND.ink500 }}>
                      {kpis.nombrePerdusAPortee >= MIN_PERDUS
                        ? 'Effort médian sur les perdus à portée'
                        : `Pas assez de données (${kpis.nombrePerdusAPortee}/${MIN_PERDUS} perdus à portée)`}
                    </div>
                    <div className="text-[11px] tabular-nums" style={{ color: BRAND.ink500 }}>
                      {kpis.nombrePerdusAPortee} à portée · {kpis.nombrePerdusHorsPortee} hors de portée
                      {kpis.nombrePerdusHorsPrix > 0 && <> · {kpis.nombrePerdusHorsPrix} hors prix</>}
                    </div>
                  </div>
                </div>

                {diagnostic && (() => {
                  const style =
                    diagnostic.pciGlobal < 85
                      ? { bg: '#FBE3D3', bd: BRAND.escStrongBd, ic: '#9A3B0B' }
                      : diagnostic.pciGlobal < 95
                        ? { bg: '#FDF1DA', bd: BRAND.escMidBd, ic: '#C2760C' }
                        : { bg: BRAND.success50, bd: BRAND.ink200, ic: BRAND.success700 };
                  return (
                    <div className="flex gap-[10px] items-center px-4 py-[11px] border-t mt-auto rounded-b-[13px]" style={{ background: style.bg, borderColor: style.bd }}>
                      <span className="w-[26px] h-[26px] rounded-md bg-white grid place-content-center flex-shrink-0" style={{ color: style.ic }}>
                        <LightBulbIcon className="w-3.5 h-3.5" />
                      </span>
                      <span className="text-[12px] leading-snug" style={{ color: BRAND.ink900 }}>
                        <b style={{ color: style.ic }}>{diagnostic.titre}</b>
                        {' — '}
                        {diagnostic.recommandation}
                        {diagnostic.alerte && (
                          <span className="block mt-1 text-[11.5px]" style={{ color: BRAND.ink700 }}>
                            ⚠ {diagnostic.alerte}
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })()}
              </div>

            </div>
          </div>

          {/* Pilotage de la marge */}
          <div className="lg:col-span-2 bg-white border rounded-[14px] flex flex-col overflow-hidden" style={{ borderColor: BRAND.primary100, boxShadow: `inset 3px 0 0 ${BRAND.primary700}` }}>
            <div className="flex items-center gap-2 px-4 py-[11px] border-b text-[11px] font-bold uppercase tracking-wide" style={{ borderColor: BRAND.ink200, color: BRAND.ink400 }}>
              <span className="w-[22px] h-[22px] rounded-md text-white grid place-content-center" style={{ background: BRAND.primary700 }}>
                <AdjustmentsHorizontalIcon className="w-3.5 h-3.5" />
              </span>
              Pilotage de la marge
              <span className="font-medium normal-case tracking-normal" style={{ color: BRAND.ink400 }}>· et si vous aviez baissé vos prix ?</span>
              <span className="relative ml-auto font-medium normal-case tracking-normal cursor-help" style={{ color: BRAND.ink400 }}>
                <span
                  onMouseEnter={() => setHoveredTooltip('pilotage-tooltip')}
                  onMouseLeave={() => setHoveredTooltip(null)}
                >
                  simulateur
                </span>
                {hoveredTooltip === 'pilotage-tooltip' && (
                  <div className="absolute z-50 w-64 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg top-full right-0 mt-2 whitespace-normal normal-case tracking-normal font-normal">
                    Rejouez votre historique : choisissez une baisse de prix et comment la financer. Chaque point sur le curseur est un marché perdu à portée (PCI ≥ {SEUIL_HORS_PORTEE}) ; dépassez-le pour le récupérer. La baisse s'applique aussi à vos marchés déjà gagnés.
                  </div>
                )}
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 flex-1">
            {/* Colonne 1 : les 3 étapes du simulateur */}
            <div className="p-4 flex flex-col gap-3">
              {/* Étape 1 : marge habituelle (D8) */}
              <div className="flex items-center justify-between gap-3">
                <label className="text-[12.5px] font-medium" style={{ color: BRAND.ink900 }}>
                  <span className="inline-grid place-content-center w-[18px] h-[18px] mr-1.5 rounded-full text-[10.5px] font-semibold text-white align-[1px]" style={{ background: BRAND.primary700 }}>1</span>
                  Votre marge habituelle
                  <span className="block text-[10.5px] font-normal ml-[24px]" style={{ color: BRAND.ink400 }}>sur coût de revient : prix = coût × (1 + marge)</span>
                </label>
                <span className="inline-flex items-center gap-1.5">
                  <input
                    value={margeBase}
                    onChange={(e) => {
                      setMargeBase(formaterNombre(e.target.value));
                      setBaissePrix(0);
                    }}
                    className="w-16 h-8 border rounded-[7px] text-right text-[13px] px-2 outline-none"
                    style={{ borderColor: BRAND.ink300, color: BRAND.ink900 }}
                    placeholder="15"
                  />
                  <span className="text-[12px]" style={{ color: BRAND.ink500 }}>%</span>
                </span>
              </div>

              {margeBaseNum <= 0 && (
                <div className="text-sm" style={{ color: BRAND.ink400 }}>Renseignez votre marge habituelle pour activer la simulation.</div>
              )}
              {margeBaseNum > 0 && !pilotageMarge && (
                <div className="text-sm" style={{ color: BRAND.ink400 }}>
                  Pas assez de données ({kpis.nombreMarchesFermes}/{MIN_MARCHES_CLOTURES} marchés clôturés).
                </div>
              )}

              {pilotageMarge && (
                <>
                  {/* Étape 2 : comment financer la baisse (D8) */}
                  <div>
                    <div className="text-[12.5px] font-medium mb-1.5" style={{ color: BRAND.ink900 }}>
                      <span className="inline-grid place-content-center w-[18px] h-[18px] mr-1.5 rounded-full text-[10.5px] font-semibold text-white align-[1px]" style={{ background: BRAND.primary700 }}>2</span>
                      Comment financer la baisse ?
                    </div>
                    <div className="grid grid-cols-3 gap-[3px] p-[3px] rounded-[9px]" style={{ background: '#F7F7F7' }}>
                      {(['marge', 'cout', 'mix'] as const).map(mode => {
                        const actif = modeFinancement === mode;
                        return (
                          <button
                            key={mode}
                            onClick={() => choisirMode(mode)}
                            className="h-[30px] px-2 rounded-[7px] text-[12px] font-medium transition-colors"
                            style={actif ? { background: 'white', color: BRAND.primary700, fontWeight: 600, boxShadow: '0 1px 2px rgba(47,47,47,0.05)' } : { color: BRAND.ink500 }}
                          >
                            {LIBELLES_FINANCEMENT[mode].court}
                          </button>
                        );
                      })}
                    </div>
                    <div className="text-[10.5px] mt-1" style={{ color: BRAND.ink400 }}>
                      {modeFinancement === 'marge' && `Coût inchangé. Jusqu'à −${baisseMaxMode.toFixed(1)} % (marge à 0).`}
                      {modeFinancement === 'cout' && `Marge conservée à ${margeBaseNum} %. Jusqu'à −${REDUCTION_COUT_MAX} % de coût.`}
                      {modeFinancement === 'mix' && `Économies d'abord (jusqu'à −${REDUCTION_COUT_MAX} %), puis la marge. Jusqu'à −${baisseMaxMode.toFixed(1)} %.`}
                    </div>
                  </div>

                  {/* Étape 3 : de combien baisser, avec les marchés perdus sur la réglette (D5) */}
                  <div>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-[12.5px] font-medium" style={{ color: BRAND.ink900 }}>
                        <span className="inline-grid place-content-center w-[18px] h-[18px] mr-1.5 rounded-full text-[10.5px] font-semibold text-white align-[1px]" style={{ background: BRAND.primary700 }}>3</span>
                        De combien baisser vos prix ?
                      </span>
                      <span className="font-mono text-[15px] font-semibold tabular-nums" style={{ color: BRAND.primary700 }}>
                        {reglage.baisse < 0.05 ? '0 %' : `−${reglage.baisse.toFixed(1)} %`}
                      </span>
                    </div>
                    <div className="relative mt-3 h-[14px]">
                      {pilotageMarge.seuilsAPortee.map(({ marche, reductionSeuil }) => {
                        const atteint = reductionSeuil <= reglage.baisse + 0.05;
                        const horsMode = reductionSeuil > baisseMaxMode;
                        const ratio = Math.min(reductionSeuil / baisseMaxMode, 1);
                        return (
                          <span
                            key={marche.id}
                            title={`${marche.nom} — il fallait −${reductionSeuil.toFixed(1)} %${horsMode ? ' (hors d\'atteinte avec ce financement)' : ''}`}
                            className="absolute top-0 w-[11px] h-[11px] -ml-[5.5px] rounded-full border-2 cursor-help"
                            style={{
                              left: `calc(8px + (100% - 16px) * ${ratio})`,
                              background: atteint ? BRAND.success700 : 'white',
                              borderColor: atteint ? BRAND.success700 : horsMode ? BRAND.ink300 : BRAND.primary500
                            }}
                          />
                        );
                      })}
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={baisseMaxMode}
                      step={0.5}
                      value={reglage.baisse}
                      onChange={(e) => setBaissePrix(parseFloat(e.target.value))}
                      className="w-full cursor-pointer"
                      style={{ accentColor: BRAND.primary700 }}
                    />
                    <div className="flex justify-between text-[10px] tabular-nums" style={{ color: BRAND.ink400 }}>
                      <span>0 %</span>
                      <span>−{baisseMaxMode.toFixed(1)} %</span>
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[10px]" style={{ color: BRAND.ink400 }}>
                      <span><span className="inline-block w-[8px] h-[8px] rounded-full border-2 mr-1 align-[-1px]" style={{ borderColor: BRAND.primary500 }} />marché perdu à portée</span>
                      <span><span className="inline-block w-[8px] h-[8px] rounded-full mr-1 align-[-1px]" style={{ background: BRAND.success700 }} />récupéré</span>
                      <span><span className="inline-block w-[8px] h-[8px] rounded-full border-2 mr-1 align-[-1px]" style={{ borderColor: BRAND.ink300 }} />hors d'atteinte avec ce financement</span>
                      {pilotageMarge.nombreHorsPortee > 0 && <span>{pilotageMarge.nombreHorsPortee} hors de portée non affiché(s)</span>}
                    </div>
                  </div>

                  {/* Lecture en langage courant */}
                  <div className="px-3 py-2.5 rounded-[10px] text-[12.5px] leading-snug" style={{ background: BRAND.primary50, color: BRAND.ink900 }}>
                    {(() => {
                      const prochain = pilotageMarge.seuilsAPortee.find(sp => sp.reductionSeuil > reglage.baisse + 0.05);
                      const delta = pilotageMarge.margeSimulee - pilotageMarge.margeActuelle;
                      const eur = (v: number) => `${Math.round(v).toLocaleString('fr-FR')} €`;
                      if (reglage.baisse < 0.05) {
                        return prochain
                          ? <>Déplacez le curseur. Le premier marché perdu se récupère à <b>−{prochain.reductionSeuil.toFixed(1)} %</b>.</>
                          : <>Aucun marché perdu à portée à simuler.</>;
                      }
                      return (
                        <>
                          Avec <b>−{reglage.baisse.toFixed(1)} %</b> sur tous vos prix, {LIBELLES_FINANCEMENT[modeFinancement].phrase},{' '}
                          {pilotageMarge.recuperes.length > 0
                            ? <>vous auriez gagné <b>{pilotageMarge.recuperes.length} marché(s) de plus</b></>
                            : <>vous n'auriez gagné <b>aucun marché de plus</b></>}
                          {' '}et votre marge serait passée de {eur(pilotageMarge.margeActuelle)} à <b>{eur(pilotageMarge.margeSimulee)}</b>{' '}
                          (<span className={delta >= 0 ? 'text-[#0E7C3A] font-semibold' : 'text-[#9A3B0B] font-semibold'}>{delta >= 0 ? '+' : '−'}{eur(Math.abs(delta))}</span>).
                          {prochain && <> Prochain marché à −{prochain.reductionSeuil.toFixed(1)} %.</>}
                        </>
                      );
                    })()}
                  </div>
                </>
              )}
            </div>

            {/* Colonne 2 : résultat de la simulation, puis repères */}
            <div className="flex flex-col border-t lg:border-t-0 lg:border-l" style={{ borderColor: BRAND.ink200 }}>
            {pilotageMarge && (() => {
              const eur = (v: number) => `${Math.round(v).toLocaleString('fr-FR')} €`;
              const deltaMarge = pilotageMarge.margeSimulee - pilotageMarge.margeActuelle;
              const lignes: { label: string; avant: string; apres: string; delta?: { texte: string; classe: string } }[] = [
                {
                  label: 'Marchés gagnés',
                  avant: String(pilotageMarge.nombreGagnesActuel),
                  apres: String(pilotageMarge.nombreGagnesSimule),
                  delta: pilotageMarge.recuperes.length > 0 ? { texte: `+${pilotageMarge.recuperes.length}`, classe: 'text-[#0E7C3A]' } : undefined
                },
                { label: 'CA gagné', avant: eur(pilotageMarge.caActuel), apres: eur(pilotageMarge.caSimule) },
                {
                  label: 'Marge captée',
                  avant: eur(pilotageMarge.margeActuelle),
                  apres: eur(pilotageMarge.margeSimulee),
                  delta: pilotageMarge.margeActuelle > 0 && Math.round(deltaMarge) !== 0
                    ? { texte: `${deltaMarge >= 0 ? '+' : '−'}${eur(Math.abs(deltaMarge))}`, classe: getMargeDeltaColor((deltaMarge / pilotageMarge.margeActuelle) * 100) }
                    : undefined
                }
              ];
              return (
                <div style={{ background: '#F7F7F7' }}>
                  {lignes.map((l, i) => (
                    <div key={l.label} className={`flex items-baseline justify-between gap-3 px-4 py-2.5 ${i > 0 ? 'border-t' : ''}`} style={{ borderColor: BRAND.ink200 }}>
                      <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: BRAND.ink400 }}>{l.label}</span>
                      <span className="flex items-baseline gap-1.5 tabular-nums">
                        <span className="text-[12.5px]" style={{ color: BRAND.ink500 }}>{l.avant}</span>
                        <span className="text-[10px]" style={{ color: BRAND.ink400 }}>→</span>
                        <span className="text-[15px] font-semibold tracking-tight" style={{ color: BRAND.ink900 }}>{l.apres}</span>
                        {l.delta && <span className={`text-[11px] font-semibold ml-1 ${l.delta.classe}`}>{l.delta.texte}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })()}

            {pilotageMarge && pilotageMarge.recuperes.length > 0 && (
              <div className="px-4 py-3 text-[12px] border-t" style={{ color: BRAND.ink700, borderColor: BRAND.ink200 }}>
                <span className="font-medium">Vous auriez été le moins-disant sur : </span>
                {pilotageMarge.recuperes.map(m => m.nom).join(', ')}
              </div>
            )}

            {/* 4. Repères : rendement de l'effort par palier (D6) */}
            {rendementEffort && (
              <div className={`p-4 ${pilotageMarge ? 'border-t' : ''}`} style={{ borderColor: BRAND.ink200 }}>
                <div className="border rounded-[10px] overflow-hidden" style={{ borderColor: BRAND.primary100 }}>
                  <div className="flex items-center justify-between gap-2 px-3 py-2 text-[12px] font-medium" style={{ background: BRAND.primary50, color: '#3D2882' }}>
                    <span>Rendement de l'effort</span>
                    <span className="text-[10.5px] font-normal tabular-nums" style={{ color: BRAND.ink500 }}>
                      {kpis.nombreMarchesFermes} clôturés · baisse sur tous vos marchés
                    </span>
                  </div>
                  <table className="w-full text-[12px] tabular-nums">
                    <thead>
                      <tr style={{ color: BRAND.ink500 }}>
                        <th className="text-left font-semibold text-[10px] uppercase tracking-wide px-3 py-1.5">Effort</th>
                        <th className="text-right font-semibold text-[10px] uppercase tracking-wide px-3 py-1.5">Succès <span className="normal-case font-normal">(auj. {kpis.tauxSucces.toFixed(0)} %)</span></th>
                        <th className="text-right font-semibold text-[10px] uppercase tracking-wide px-3 py-1.5" style={modeFinancement === 'marge' ? { color: BRAND.primary700 } : undefined}>Net · marge</th>
                        <th className="text-right font-semibold text-[10px] uppercase tracking-wide px-3 py-1.5" style={modeFinancement === 'cout' ? { color: BRAND.primary700 } : undefined}>Net · économies</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rendementEffort.map(r => {
                        const fmtNet = (v: number) => `${v >= 0 ? '+' : '−'}${Math.round(Math.abs(v) / 1000).toLocaleString('fr-FR')} k€`;
                        const netClass = (v: number) => (v >= 0 ? 'text-[#0E7C3A] font-semibold' : 'text-[#9A3B0B]');
                        return (
                          <tr
                            key={r.palier}
                            className="border-t cursor-pointer hover:bg-[#F6F2FF]"
                            style={{ borderColor: BRAND.ink200, background: Math.abs(reglage.baisse - r.palier) < 0.05 ? BRAND.primary50 : undefined }}
                            title="Appliquer cet effort au simulateur"
                            onClick={() => setBaissePrix(Math.min(r.palier, baisseMaxMode))}
                          >
                            <td className="px-3 py-1.5 font-semibold" style={{ color: BRAND.ink900 }}>−{r.palier} %</td>
                            <td className="px-3 py-1.5 text-right whitespace-nowrap" style={{ color: BRAND.ink900 }}>
                              {r.tauxSucces.toFixed(0)} %
                              <span className="ml-1 text-[11px]" style={{ color: BRAND.ink500 }}>({r.marchesEnPlus > 0 ? `+${r.marchesEnPlus}` : '0'})</span>
                            </td>
                            <td className={`px-3 py-1.5 text-right ${netClass(r.netLevierMarge)}`} style={modeFinancement === 'marge' ? { background: 'rgba(124,99,208,0.06)' } : undefined}>{fmtNet(r.netLevierMarge)}</td>
                            <td className={`px-3 py-1.5 text-right ${netClass(r.netLevierCout)}`} style={modeFinancement === 'cout' ? { background: 'rgba(124,99,208,0.06)' } : undefined}>{fmtNet(r.netLevierCout)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="px-3 py-2 text-[10.5px] border-t" style={{ borderColor: BRAND.ink200, color: BRAND.ink400 }}>
                    Net = marge des marchés récupérés − marge perdue sur les marchés déjà gagnés. Cliquez une ligne pour l'appliquer au simulateur. Lecture de l'historique, concurrents supposés inchangés.
                  </div>
                </div>
              </div>
            )}
            </div>
            </div>
          </div>
        </div>

        {/* ══ LISTE DES MARCHÉS ══ */}
        <div className="bg-white border rounded-[14px] overflow-hidden" style={{ borderColor: BRAND.ink200 }}>
          <div className="flex items-center gap-3.5 px-4 py-3 border-b flex-wrap" style={{ borderColor: BRAND.ink200 }}>
            <div className="flex gap-[3px] p-[3px] rounded-[9px]" style={{ background: '#F7F7F7' }}>
              {(['Tous', 'Perdu', 'Gagné', 'En attente'] as const).map(seg => {
                const count = seg === 'Tous' ? marches.length : marches.filter(m => m.resultat === seg).length;
                const active = filtreResultat === seg;
                const activeColor = seg === 'Gagné' ? BRAND.success700 : seg === 'Perdu' ? '#C2760C' : BRAND.ink900;
                const chipBg = seg === 'Gagné' ? BRAND.success50 : seg === 'Perdu' ? '#FDF1DA' : BRAND.primary100;
                const chipColor = seg === 'Gagné' ? BRAND.success700 : seg === 'Perdu' ? '#C2760C' : BRAND.primary700;
                return (
                  <button
                    key={seg}
                    onClick={() => { setFiltreResultat(seg); setPage(1); }}
                    className="inline-flex items-center gap-1.5 h-[30px] px-3 rounded-[7px] text-[12.5px] font-medium transition-colors"
                    style={active ? { background: 'white', color: activeColor, fontWeight: 600, boxShadow: '0 1px 2px rgba(47,47,47,0.05)' } : { color: BRAND.ink500 }}
                  >
                    <span>{seg}</span>
                    <span
                      className="font-mono text-[10.5px] px-1.5 py-px rounded-full"
                      style={active ? { background: chipBg, color: chipColor } : { background: BRAND.ink200, color: BRAND.ink500 }}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="ml-auto flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-2 h-[34px] px-3 border rounded-[8px] bg-white w-[200px]" style={{ borderColor: BRAND.ink200 }}>
                <MagnifyingGlassIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: BRAND.ink400 }} />
                <input
                  value={recherche}
                  onChange={(e) => { setRecherche(e.target.value); setPage(1); }}
                  placeholder="Rechercher…"
                  className="border-none outline-none bg-transparent text-[13px] flex-1 min-w-0"
                  style={{ color: BRAND.ink900 }}
                />
              </div>
              <button
                className="inline-flex items-center gap-1.5 h-[34px] px-3 border rounded-[8px] bg-white text-[12.5px] hover:bg-gray-50 transition-colors"
                style={{ borderColor: BRAND.ink300, color: BRAND.ink900 }}
                title="Filtres avancés non câblés — design uniquement"
              >
                <FunnelIcon className="w-3.5 h-3.5" style={{ color: BRAND.ink500 }} />
                Filtrer
              </button>
              <button
                disabled
                className="inline-flex items-center gap-2 h-[34px] px-3.5 rounded-[8px] text-white text-[12.5px] font-medium cursor-not-allowed"
                style={{ background: BRAND.ink300 }}
              >
                <PlusIcon className="w-3.5 h-3.5" />
                Ajouter
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr>
                  <th className="w-10 pl-4 py-2.5">
                    <input
                      type="checkbox"
                      checked={marchesPage.length > 0 && marchesPage.every(m => selectedIds.has(m.id))}
                      onChange={() => {
                        const allSelected = marchesPage.every(m => selectedIds.has(m.id));
                        setSelectedIds(prev => {
                          const next = new Set(prev);
                          marchesPage.forEach(m => (allSelected ? next.delete(m.id) : next.add(m.id)));
                          return next;
                        });
                      }}
                      className="w-4 h-4 rounded"
                      style={{ accentColor: BRAND.primary700 }}
                    />
                  </th>
                  <th className="text-left text-[10px] font-semibold uppercase tracking-wide px-3.5 py-2.5" style={{ color: BRAND.ink500 }}>Marché</th>
                  <th className="text-right text-[10px] font-semibold uppercase tracking-wide px-3.5 py-2.5" style={{ color: BRAND.ink500 }}>Ton prix</th>
                  <th className="text-left text-[10px] font-semibold uppercase tracking-wide px-3.5 py-2.5" style={{ color: BRAND.ink500 }}>Résultat</th>
                  <th className="text-right text-[10px] font-semibold uppercase tracking-wide px-3.5 py-2.5" style={{ color: BRAND.ink500 }} title="Perdu : prix de l'attributaire s'il est communiqué, sinon prix le plus bas estimé via votre note prix. Gagné : votre prix.">Prix de référence</th>
                  <th
                    onClick={() => toggleTri('ecart')}
                    className="text-right text-[10px] font-semibold uppercase tracking-wide px-3.5 py-2.5 cursor-pointer select-none"
                    style={{ color: BRAND.ink500 }}
                  >
                    Écart requis
                    {triColonne === 'ecart' && <span className="ml-1" style={{ color: BRAND.primary700 }}>{triSens === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <th
                    onClick={() => toggleTri('pci')}
                    className="text-right text-[10px] font-semibold uppercase tracking-wide px-3.5 py-2.5 cursor-pointer select-none"
                    style={{ color: BRAND.ink500 }}
                  >
                    PCI
                    {triColonne === 'pci' && <span className="ml-1" style={{ color: BRAND.primary700 }}>{triSens === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <th className="px-3.5 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {marchesPage.map((marche) => {
                  const pci = pciPerdu(marche);
                  const effortRequis = calculerEffortRequis(marche);
                  const tier = pci !== null ? tierFromPci(pci) : null;
                  const peutChallenger = marche.resultat === 'Perdu' && marche.prixGagnant === undefined;
                  return (
                    <tr
                      key={marche.id}
                      className="border-b cursor-pointer hover:bg-gray-50 group transition-colors"
                      style={{ borderColor: BRAND.ink200 }}
                      onClick={() => handleCompleter(marche)}
                    >
                      <td className="pl-4 py-[11px]" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(marche.id)}
                          onChange={() => toggleSelect(marche.id)}
                          className="w-4 h-4 rounded"
                          style={{ accentColor: BRAND.primary700 }}
                        />
                      </td>
                      <td className="px-3.5 py-[11px]">
                        <div className="font-semibold tracking-tight" style={{ color: BRAND.ink900 }}>{marche.nom}</div>
                        <div className="text-[11.5px] mt-0.5" style={{ color: BRAND.ink500 }}>
                          {marche.acheteur}
                          {marche.dateRemise
                            ? <span style={{ color: BRAND.ink400 }}> · remis le {formaterDate(marche.dateRemise)}</span>
                            : <span style={{ color: '#C2760C' }}> · date de remise manquante</span>}
                        </div>
                      </td>
                      <td className="px-3.5 py-[11px] text-right font-medium tabular-nums" style={{ color: BRAND.ink900 }}>
                        {marche.tonPrix.toLocaleString('fr-FR')} €
                      </td>
                      <td className="px-3.5 py-[11px]">
                        <span
                          className="inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2.5 py-0.5 rounded-full"
                          style={
                            marche.resultat === 'Gagné'
                              ? { background: BRAND.success50, color: BRAND.success700 }
                              : marche.resultat === 'Perdu'
                                ? { background: BRAND.escGrayBg, color: BRAND.ink700, border: `1px solid ${BRAND.ink200}` }
                                : { background: '#F7F7F7', color: BRAND.ink500 }
                          }
                        >
                          {marche.resultat === 'En attente'
                            ? <ClockIcon className="w-2.5 h-2.5" />
                            : <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'currentColor' }} />}
                          {estPerduHorsPrix(marche) ? 'Perdu hors prix' : marche.resultat}
                        </span>
                      </td>
                      <td className="px-3.5 py-[11px] text-right font-medium tabular-nums" style={{ color: BRAND.ink900 }}>
                        {marche.prixGagnant ? `${Math.round(marche.prixGagnant).toLocaleString('fr-FR')} €` : <span style={{ color: BRAND.ink300 }}>—</span>}
                        {marche.resultat === 'Perdu' && marche.sourcePrixReference && (
                          <div className="text-[10.5px] font-normal" style={{ color: BRAND.ink400 }}>
                            {marche.sourcePrixReference === 'communique' ? 'communiqué' : 'estimé via note prix'}
                          </div>
                        )}
                      </td>
                      <td className="px-3.5 py-[11px] text-right tabular-nums">
                        {effortRequis && tier ? (
                          <span className={`inline-flex items-baseline gap-1.5 font-semibold justify-end ${tierTextClass(tier)}`}>
                            −{effortRequis.pct.toFixed(1)} %
                            <span className="font-normal text-[11.5px]" style={{ color: BRAND.ink500 }}>−{effortRequis.eur.toLocaleString('fr-FR')} €</span>
                          </span>
                        ) : (
                          <span style={{ color: BRAND.ink300 }}>—</span>
                        )}
                      </td>
                      <td className="px-3.5 py-[11px] text-right">
                        {pci !== null && tier ? (
                          <span className={`inline-grid place-content-center min-w-[32px] h-[23px] px-2 rounded-md font-semibold text-[12.5px] tabular-nums ${tierChipClass(tier)}`}>
                            {pci.toFixed(0)}
                          </span>
                        ) : (
                          <span style={{ color: BRAND.ink300 }}>—</span>
                        )}
                      </td>
                      <td className="px-3.5 py-[11px]">
                        <div className="flex items-center gap-1.5 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                          {peutChallenger && (
                            <button
                              onClick={(e) => handleOpenCourrierModal(marche, e)}
                              className="inline-flex items-center gap-1.5 h-[27px] px-2.5 border rounded-[7px] text-[11.5px] font-medium transition-colors"
                              style={{ borderColor: BRAND.primary300, background: BRAND.primary50, color: BRAND.primary700 }}
                              title="Générer le courrier de demande des notes"
                            >
                              <BoltIcon className="w-3 h-3" />
                              Challenger
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); handleCompleter(marche); }}
                            className="w-[27px] h-[27px] rounded-[7px] border bg-white grid place-content-center transition-colors"
                            style={{ borderColor: BRAND.ink200, color: BRAND.ink500 }}
                            title="Modifier le marché"
                          >
                            <EllipsisHorizontalIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {marchesPage.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-sm" style={{ color: BRAND.ink400 }}>
                      Aucun marché ne correspond à ce filtre.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center gap-4 px-4 py-[11px] border-t text-[11.5px] flex-wrap" style={{ borderColor: BRAND.ink200, color: BRAND.ink500 }}>
            <div className="flex items-center gap-2.5 flex-wrap">
              <span className="font-semibold uppercase tracking-wide text-[10px]">Écart requis</span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm border" style={{ background: BRAND.escGrayBg, borderColor: BRAND.ink200 }} />
                Faible
              </span>
              <span style={{ color: BRAND.ink300 }}>→</span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm border" style={{ background: '#FDF1DA', borderColor: BRAND.escMidBd }} />
                Modéré
              </span>
              <span style={{ color: BRAND.ink300 }}>→</span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm border" style={{ background: '#FBE3D3', borderColor: BRAND.escStrongBd }} />
                Important
              </span>
            </div>
            <div className="ml-auto flex items-center gap-1.5">
              <span className="mr-1.5">
                {marchesAffiches.length === 0 ? 0 : (pageActuelle - 1) * PAGE_SIZE + 1}–{Math.min(pageActuelle * PAGE_SIZE, marchesAffiches.length)} sur {marchesAffiches.length}
              </span>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={pageActuelle <= 1}
                className="w-7 h-7 rounded-[7px] border bg-white grid place-content-center disabled:opacity-40"
                style={{ borderColor: BRAND.ink200, color: BRAND.ink500 }}
              >
                <ChevronLeftIcon className="w-3 h-3" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className="w-7 h-7 rounded-[7px] border text-[11px] transition-colors"
                  style={p === pageActuelle
                    ? { background: BRAND.primary700, borderColor: BRAND.primary700, color: 'white' }
                    : { borderColor: BRAND.ink200, background: 'white', color: BRAND.ink500 }}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={pageActuelle >= totalPages}
                className="w-7 h-7 rounded-[7px] border bg-white grid place-content-center disabled:opacity-40"
                style={{ borderColor: BRAND.ink200, color: BRAND.ink500 }}
              >
                <ChevronRightIcon className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>

        {/* Modal de complétion */}
        {isModalOpen && selectedMarche && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Compléter le marché</h2>
                  <p className="text-sm text-gray-600 mt-1">{selectedMarche.nom}</p>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Formulaire */}
              <div className="space-y-6">
                {/* Résultat */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Résultat *
                  </label>
                  <select
                    value={formData.resultat}
                    onChange={(e) => setFormData({ ...formData, resultat: e.target.value as MarcheResultat })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  >
                    <option value="Gagné">Gagné</option>
                    <option value="Perdu">Perdu</option>
                  </select>
                </div>

                {/* Votre prix */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Votre prix *
                  </label>
                  <input
                    type="text"
                    value={formData.tonPrix}
                    onChange={(e) => setFormData({ ...formData, tonPrix: formaterNombre(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                    placeholder="0"
                  />
                </div>

                {/* Date de remise */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date de remise *
                  </label>
                  <input
                    type="date"
                    value={formData.dateRemise}
                    onChange={(e) => setFormData({ ...formData, dateRemise: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>

                {/* Note prix obtenue (seulement si Perdu) */}
                {formData.resultat === 'Perdu' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Prix de l'attributaire (si connu)
                      </label>
                      <input
                        type="text"
                        value={formData.prixAttributaire}
                        onChange={(e) => setFormData({ ...formData, prixAttributaire: formaterNombre(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                        placeholder="0"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Montant de l'offre retenue (courrier de rejet, avis d'attribution, données essentielles). Prioritaire sur l'estimation via la note prix ci-dessous.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mode de notation du marché *
                      </label>
                      <select
                        value={formData.modeNotation}
                        onChange={(e) => setFormData({ ...formData, modeNotation: e.target.value as PriceFormula })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                      >
                        {(Object.keys(MODE_NOTATION_LABELS) as PriceFormula[]).map((mode) => (
                          <option key={mode} value={mode}>{MODE_NOTATION_LABELS[mode]}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Note prix obtenue
                      </label>
                      <input
                        type="text"
                        value={formData.notePrixObtenue}
                        onChange={(e) => setFormData({ ...formData, notePrixObtenue: formaterNombre(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                        placeholder="0"
                      />
                    </div>

                    {/* Note prix maximale */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Note prix maximale
                      </label>
                      <input
                        type="text"
                        value={formData.notePrixMaximale}
                        onChange={(e) => setFormData({ ...formData, notePrixMaximale: formaterNombre(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                        placeholder="0"
                      />
                    </div>
                  </>
                )}

                {/* Bouton Générer RAO (si Perdu et notes saisies dans le formulaire) */}
                {formData.resultat === 'Perdu' &&
                 formData.notePrixObtenue &&
                 formData.notePrixMaximale && (
                  <div className="pt-4 border-t border-gray-200">
                    <button
                      onClick={() => {
                        const notePrixObtenue = parseFloat(formData.notePrixObtenue.replace(/\s/g, '').replace(',', '.'));
                        const notePrixMaximale = parseFloat(formData.notePrixMaximale.replace(/\s/g, '').replace(',', '.'));
                        const tonPrix = parseFloat(formData.tonPrix.replace(/\s/g, '').replace(',', '.'));

                        if (isNaN(notePrixObtenue) || isNaN(notePrixMaximale) || isNaN(tonPrix)) {
                          alert('Veuillez remplir tous les champs avant de générer la demande');
                          return;
                        }

                        const texte = `Objet : Demande de communication des notes de l'appel d'offres "${selectedMarche.nom}"

Madame, Monsieur,

Dans le cadre de l'appel d'offres "${selectedMarche.nom}" lancé par ${selectedMarche.acheteur}, je souhaiterais obtenir la communication des notes obtenues pour les différents critères d'évaluation, et notamment la note prix.

Notre offre s'élevait à ${tonPrix.toLocaleString('fr-FR')} €.

Je vous remercie par avance pour votre retour.

Cordialement`;

                        setRaoText(texte);
                        setShowRaoModal(true);
                      }}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <ClipboardDocumentIcon className="w-5 h-5" />
                      <span>Générer la demande de communication des notes</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  className="px-6 py-2 text-white rounded-lg transition-colors"
                  style={{ background: BRAND.primary700 }}
                >
                  Sauvegarder
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Courrier d'attribution */}
        {showCourrierModal && selectedMarcheForCourrier && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Courrier d'attribution</h2>
                <button
                  onClick={() => {
                    setShowCourrierModal(false);
                    setSelectedMarcheForCourrier(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-4 p-3 rounded-lg border" style={{ background: BRAND.primary50, borderColor: BRAND.primary100 }}>
                <p className="text-sm text-gray-700">
                  <strong>Marché:</strong> {selectedMarcheForCourrier.nom}
                </p>
                <p className="text-sm text-gray-700 mt-1">
                  <strong>Acheteur:</strong> {selectedMarcheForCourrier.acheteur}
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <textarea
                  readOnly
                  value={courrierText}
                  className="w-full h-96 p-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-violet-500 font-mono text-sm"
                />
              </div>

              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCourrierModal(false);
                    setSelectedMarcheForCourrier(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Fermer
                </button>
                <button
                  onClick={handleCopierCourrier}
                  className="px-6 py-2 text-white rounded-lg transition-colors flex items-center space-x-2"
                  style={{ background: BRAND.primary700 }}
                >
                  <ClipboardDocumentIcon className="w-5 h-5" />
                  <span>Copier</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal RAO */}
        {showRaoModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Demande de communication des notes</h2>
                <button
                  onClick={() => setShowRaoModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <textarea
                  readOnly
                  value={raoText}
                  className="w-full h-64 p-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>

              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setShowRaoModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Fermer
                </button>
                <button
                  onClick={handleCopierRao}
                  className="px-6 py-2 text-white rounded-lg transition-colors flex items-center space-x-2"
                  style={{ background: BRAND.primary700 }}
                >
                  <ClipboardDocumentIcon className="w-5 h-5" />
                  <span>Copier</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformanceMarches;
